import React, { useState, useMemo } from 'react';
import { Receipt, SortConfig, SortKey } from '../types';
import { JsonViewerModal } from './JsonViewerModal';
import { FilePreviewModal } from './FilePreviewModal';
import { SortIcon } from './icons/SortIcon';
import { ReceiptRow } from './ReceiptRow';

interface ReceiptsTableProps {
  receipts: Receipt[];
  isEditMode: boolean;
  onUpdate: (receipt: Receipt) => void;
  onDelete: (receipt: Receipt) => void;
  sortConfig: SortConfig | null;
  onSort: (key: SortKey) => void;
  selectedReceiptIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
}

export const ReceiptsTable: React.FC<ReceiptsTableProps> = ({ 
    receipts, 
    isEditMode, 
    onUpdate, 
    onDelete, 
    sortConfig, 
    onSort,
    selectedReceiptIds,
    onToggleSelect,
    onToggleSelectAll,
}) => {
  const [viewingJson, setViewingJson] = useState<string | null>(null);
  const [viewingFile, setViewingFile] = useState<Receipt | null>(null);

  const areAllOnPageSelected = useMemo(() => {
      if (receipts.length === 0) return false;
      return receipts.every(r => selectedReceiptIds.has(r.id));
  }, [receipts, selectedReceiptIds]);

  const SortableHeader: React.FC<{ sortKey: SortKey, children: React.ReactNode }> = ({ sortKey, children }) => (
    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        <button className="flex items-center gap-1 hover:text-gray-700" onClick={() => onSort(sortKey)}>
            {children}
            <SortIcon direction={sortConfig?.key === sortKey ? sortConfig.direction : null} />
        </button>
    </th>
  );
  
  if (receipts.length === 0) {
    return <div className="text-center text-gray-500 py-10">Žádné účtenky k zobrazení pro daný filtr.</div>;
  }

  return (
    <>
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {isEditMode && (
                  <th scope="col" className="px-6 py-3">
                    <input
                        type="checkbox"
                        checked={areAllOnPageSelected}
                        onChange={onToggleSelectAll}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        aria-label="Vybrat všechny na stránce"
                    />
                  </th>
              )}
              <SortableHeader sortKey="receiptDate">Datum účtenky</SortableHeader>
              <SortableHeader sortKey="businessName">Podnik</SortableHeader>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">                
                  <button className="flex items-center gap-1 hover:text-gray-700" onClick={() => onSort('amount')}>
                      Částka
                      <SortIcon direction={sortConfig?.key === 'amount' ? sortConfig.direction : null} />
                  </button>
              </th>
              <SortableHeader sortKey="category">Kategorie</SortableHeader>
              <SortableHeader sortKey="paymentMethod">Způsob platby</SortableHeader>
              <SortableHeader sortKey="tags">Štítky</SortableHeader>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Akce
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {receipts.map((receipt) => (
              <ReceiptRow
                key={receipt.id}
                receipt={receipt}
                isEditMode={isEditMode}
                isSelected={selectedReceiptIds.has(receipt.id)}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onToggleSelect={onToggleSelect}
                onViewJson={setViewingJson}
                onViewFile={setViewingFile}
              />
            ))}
          </tbody>
        </table>
      </div>
      {viewingJson && (
        <JsonViewerModal jsonString={viewingJson} onClose={() => setViewingJson(null)} />
      )}
      {viewingFile && (() => {
          let finalFileName = viewingFile.fileName; // fallback
          try {
              const jsonData = JSON.parse(viewingFile.jsonData);
              const suggestedName = jsonData.suggestedFileName;
              // Použijeme originalFileName z JSON jako spolehlivý zdroj koncovky
              const originalNameWithExt = jsonData.originalFileName || viewingFile.fileName;
              
              if (suggestedName && typeof suggestedName === 'string') {
                  const lastDotIndex = originalNameWithExt.lastIndexOf('.');
                  if (lastDotIndex > 0 && lastDotIndex < originalNameWithExt.length - 1) {
                      const extension = originalNameWithExt.substring(lastDotIndex); // např. ".pdf"
                      // suggestedName je nyní autorita pro základ jména, jen připojíme koncovku
                      finalFileName = suggestedName + extension;
                  } else {
                      // Původní soubor neměl koncovku, použijeme navržené jméno tak, jak je
                      finalFileName = suggestedName;
                  }
              }
          } catch (e) {
              console.warn("Could not parse jsonData to construct file name", e);
          }

          return (
              <FilePreviewModal
                  isOpen={!!viewingFile}
                  onClose={() => setViewingFile(null)}
                  fileBlob={viewingFile.fileBlob}
                  fileUrl={viewingFile.fileUrl}
                  fileName={finalFileName}
              />
          );
      })()}
    </>
  );
};